import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency as formatCurrencyFromConstants } from "../../constants/currency";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  LinearProgress,
  Tooltip,
  Stack,
  Avatar,
  Badge,
  Divider,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  InputBase,
  Select,
  FormControl,
  InputLabel,
  Grid,
  alpha,
  useTheme,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  LocalShipping as TruckIcon,
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Engineering as InProgressIcon,
  Flag as PriorityIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { RootState } from "../../store";
import { Order, ItemStatus } from "./order";
import { formatDate, formatStatusText } from "../../utils/formatters";
import { usePermission } from "../../components/common/PermissionGuard";

interface CompactOrderListProps {
  orders: Order[];
  loading?: boolean;
  onEdit?: (order: Order) => void;
  onView?: (orderId: string) => void;
  onPrint?: (order: Order) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  filterStatus?: string;
  onStatusFilterChange?: (status: string) => void;
  filterPriority?: string;
  onPriorityFilterChange?: (priority: string) => void;
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  showFilters?: boolean;
  showActions?: boolean;
  compactMode?: boolean;
  selectedOrderId?: string | null;
}

const CompactOrderList: React.FC<CompactOrderListProps> = ({
  orders = [],
  loading = false,
  onEdit,
  onView,
  onPrint,
  onRefresh,
  onExport,
  searchTerm = "",
  onSearchChange,
  filterStatus = "all",
  onStatusFilterChange,
  filterPriority = "all",
  onPriorityFilterChange,
  sortBy = "dueDate",
  onSortChange,
  showFilters = true,
  showActions = true,
  compactMode = false,
  selectedOrderId = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const selectedOrderRef = useRef<string | null>(null);

  // Scroll to selected order when it changes
  useEffect(() => {
    if (selectedOrderId && selectedOrderId !== selectedOrderRef.current) {
      selectedOrderRef.current = selectedOrderId;
      setTimeout(() => {
        const element = document.getElementById(`order-${selectedOrderId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [selectedOrderId]);

  const canEdit = usePermission("orders", "update");
  const canView = usePermission("orders", "read");

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ClockIcon fontSize="small" sx={{ color: "warning.main" }} />;
      case "in_progress":
        return <InProgressIcon fontSize="small" sx={{ color: "info.main" }} />;
      case "completed":
        return <CheckIcon fontSize="small" sx={{ color: "success.main" }} />;
      case "dispatched":
        return <TruckIcon fontSize="small" sx={{ color: "secondary.main" }} />;
      default:
        return <ClockIcon fontSize="small" sx={{ color: "text.secondary" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "info";
      case "completed":
        return "success";
      case "dispatched":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityIcon = (priority: string) => {
    const colors = {
      urgent: theme.palette.error.main,
      high: theme.palette.warning.main,
      medium: theme.palette.info.main,
      low: theme.palette.success.main,
    };
    return (
      <PriorityIcon
        fontSize="small"
        sx={{ color: colors[priority as keyof typeof colors] || theme.palette.text.secondary }}
      />
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, order: Order) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleAction = (action: string) => {
    if (!selectedOrder) return;

    switch (action) {
      case "view":
        onView?.(selectedOrder.id);
        break;
      case "edit":
        onEdit?.(selectedOrder);
        break;
      case "print":
        onPrint?.(selectedOrder);
        break;
      case "qr":
        // Handle QR code action
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const getOrderSummary = (order: Order) => {
    // Calculate total items from products if not available
    const calculateTotalItems = (order: any) => {
      if (order.totalItems) return order.totalItems;
      
      if (!order.products || order.products.length === 0) return 0;
      
      return order.products.reduce((total: number, product: any) => {
        const allItems = [
          ...(product.frame || []),
          ...(product.panel || []),
          ...(product.panelWindow || []),
          ...(product.net || []),
          ...(product.openableType || []),
          ...(product.items || [])
        ];
        return total + allItems.length;
      }, 0);
    };

    const totalItems = calculateTotalItems(order);
    const completedItems = order.completedItems || 0;
    const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    
    return {
      totalItems,
      completedItems,
      progress,
      remainingItems: totalItems - completedItems,
    };
  };

  const sortedOrders = useMemo(() => {
    if (!orders.length) return [];
    
    return [...orders].sort((a, b) => {
      switch (sortBy) {
        case "createdAt":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "dueDate":
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case "priority":
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) - 
                 (priorityOrder[a.priority as keyof typeof priorityOrder] || 0);
        case "status":
          const statusOrder = { pending: 1, in_progress: 2, completed: 3, dispatched: 4 };
          return (statusOrder[a.status as keyof typeof statusOrder] || 0) - 
                 (statusOrder[b.status as keyof typeof statusOrder] || 0);
        case "customer":
          return a.customer?.name?.localeCompare(b.customer?.name || "") || 0;
        case "amount":
          return (b.totalAmount as number) - (a.totalAmount as number);
        default:
          return 0;
      }
    });
  }, [orders, sortBy]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <Card sx={{ height: compactMode ? 200 : 280 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 40, height: 40, bgcolor: 'grey.300', borderRadius: '50%', mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 20, bgcolor: 'grey.300', borderRadius: 1, mb: 1 }} />
                      <Box sx={{ height: 16, bgcolor: 'grey.300', borderRadius: 1, width: '60%' }} />
                    </Box>
                  </Box>
                  <Box sx={{ height: 8, bgcolor: 'grey.300', borderRadius: 1, mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ height: 16, bgcolor: 'grey.300', borderRadius: 1, width: '40%' }} />
                    <Box sx={{ height: 16, bgcolor: 'grey.300', borderRadius: 1, width: '30%' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Filters and Search */}
      {showFilters && (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'relative' }}>
                <SearchIcon
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    fontSize: 20,
                  }}
                />
                <InputBase
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  sx={{
                    pl: 5,
                    pr: 2,
                    py: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    width: '100%',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused': {
                      borderColor: 'primary.main',
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => onStatusFilterChange?.(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="dispatched">Dispatched</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => onPriorityFilterChange?.(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => onSortChange?.(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Created Date</MenuItem>
                  <MenuItem value="dueDate">Due Date</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} md={2}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Refresh">
                  <IconButton onClick={onRefresh} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export">
                  <IconButton onClick={onExport} size="small">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Orders Grid */}
      <Grid container spacing={2}>
        {sortedOrders.map((order) => {
          const summary = getOrderSummary(order);
          const isExpanded = expandedOrders.has(order.id);
          const overdue = isOverdue(order.dueDate);

          const isSelected = selectedOrderId === order.id;
          
          return (
            <Grid item xs={12} sm={6} lg={4} key={order.id}>
              <Card
                id={`order-${order.id}`}
                sx={{
                  height: compactMode ? 200 : 280,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  bgcolor: isSelected 
                    ? alpha(theme.palette.primary.main, 0.08) 
                    : 'background.paper',
                  border: '1px solid',
                  borderColor: isSelected 
                    ? theme.palette.primary.main 
                    : 'divider',
                  borderLeft: isSelected 
                    ? `4px solid ${theme.palette.primary.main}` 
                    : '1px solid',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8],
                    bgcolor: isSelected 
                      ? alpha(theme.palette.primary.main, 0.12) 
                      : 'background.paper',
                  },
                }}
                onClick={() => onView?.(order.id)}
              >
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        width: 40,
                        height: 40,
                        mr: 2,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {order.customer?.name?.charAt(0)?.toUpperCase() || 'O'}+
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Order
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {order.customer?.name}
                      </Typography>
                    </Box>
                    {showActions && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, order)}
                        sx={{ ml: 1 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* Status and Priority */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={getStatusIcon(order.status)}
                      label={formatStatusText(order.status)}
                      size="small"
                      color={getStatusColor(order.status) as any}
                      variant="outlined"
                    />
                    <Chip
                      icon={getPriorityIcon(order.priority)}
                      label={order.priority}
                      size="small"
                      color={getPriorityColor(order.priority) as any}
                      variant="outlined"
                    />
                    {overdue && (
                      <Chip
                        label="Overdue"
                        size="small"
                        color="error"
                        variant="filled"
                      />
                    )}
                  </Box>

                  {/* Progress */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {summary.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={summary.progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {summary.completedItems}/{summary.totalItems} items
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {summary.remainingItems} remaining
                      </Typography>
                    </Box>
                  </Box>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        {order.products?.length || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Products
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="secondary.main" fontWeight="bold">
                        {formatCurrencyFromConstants(order.totalAmount || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Amount
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="text.primary" fontWeight="bold">
                        {formatDate(order.dueDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Due Date
                      </Typography>
                    </Box>
                  </Box>

                  {/* Footer Actions */}
                  {!compactMode && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDate(order.createdAt as string)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {canView && (
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView?.(order.id);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEdit && (
                            <Tooltip title="Edit Order">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit?.(order);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Print QR Codes">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onPrint?.(order);
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Empty State */}
      {sortedOrders.length === 0 && !loading && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 2,
          }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterStatus !== 'all' || filterPriority !== 'all'
              ? 'Try adjusting your filters or search terms'
              : 'Create your first order to get started'}
          </Typography>
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={() => handleAction('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Order</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction('print')}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print QR Codes</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction('qr')}>
          <ListItemIcon>
            <QrCodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View QR Codes</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CompactOrderList;
